import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ClearIcon from '@mui/icons-material/Clear';
import {
    alpha,
    Button,
    ButtonGroup,
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { LocalizationProvider, MobileDatePicker, MobileDateTimePicker, MobileTimePicker } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import moment from 'moment';
import { ChangeEvent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import useInventory, { ContentInventory, InventoryVersion } from '../../../../hooks/useInventory';
import { OnlyFansMassDMMessageSchedule, OnlyFansMassDMMessageScheduleSchema } from '../../../../hooks/useMessageUsersMassDmList';
import useSextforceBigBrotherRestrictedWords from '../../../../hooks/useSextforceBigBrotherRestrictedWords';
import useSextforceCaptions from '../../../../hooks/useSextforceCaptions';
import { OnlyFansSubscriber } from '../../../../hooks/useSubscriber';
import useSubscriberLists from '../../../../hooks/useSubscriberLists';
import useSubscriberMedia from '../../../../hooks/useSubscriberMedia';
import { OnlyFansVaultMedias } from '../../../../hooks/useSubscriberVaultAlbums';
import useSubscribersVaultMediasAdditonal from '../../../../hooks/useSubscriberVaultMediasAdditional';
import { OnlyFansLists } from '../../../../types/onlyFansTypes';
import { parseOnlyFansListId } from '../../../../utils/common';
import { getRestrictedWordsInMessage } from '../../../../utils/messageUsers';
import InventoryTemplateTagsSelector from '../../../common/InventoryTemplateTagsSelector';
import InventoryTemplateVariantsSelector from '../../../common/InventoryTemplateVariantsSelector';
import OnlyFansAlbumSelector from '../../../common/OnlyFansAlbumSelector';
import TemplateModeToggle, { TemplateSubMode } from '../../../common/TemplateModeToggle';
import OnlyFansListsMultiSelector from '../../../common/OnlyFansListsMultiSelector';
import OnlyFansListsSelector from '../../../common/OnlyFansListsSelector';
import OnlyFansMediaSelector from '../../../common/OnlyFansMediaSelector';
import OnlyFansMessageEditorWithPreview from '../../../common/OnlyFansMessageEditorWithPreview';
import OnlyFansTagCreatorsSelector from '../../../common/OnlyFansTagCreatorsSelector';
import SextforceColorSelector from '../../../common/SextforceColorSelector';
import SelectTimezone from '../../../forms/helpers/SelectTimezone';
import QuickSelectInventory from '../../../inventory/QuickSelectInventory';
import MessageUsersPriceLockAndPreviewSelector from '../sendMessage/MessageUsersPriceLockAndPreviewSelector';

const TitleGridItem = (props: { title: string | ReactNode; description?: string | ReactNode }) => {
    const { title, description } = props;

    return (
        <Grid item xs={12}>
            <Typography variant="inherit">{title}</Typography>
            {description && (
                <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                    {description}
                </Typography>
            )}
        </Grid>
    );
};

const TitleGridItemWithCheckBox = (props: {
    title: string | ReactNode;
    description?: string | ReactNode;
    checked: boolean;
    setChecked: (disabled: boolean) => void;
    disabled?: boolean;
}) => {
    const { title, description, checked, setChecked, disabled } = props;

    return (
        <Grid item xs={12}>
            <FormControlLabel
                control={
                    <Checkbox
                        size="medium"
                        checked={checked}
                        disabled={disabled}
                        onChange={(e, checked) => {
                            setChecked(checked);
                        }}
                    />
                }
                label={
                    <Typography variant="inherit" color={disabled ? 'text.disabled' : 'text.primary'}>
                        {title}
                    </Typography>
                }
            />
            {description && (
                <Typography variant="body2" color={disabled ? 'text.disabled' : 'text.secondary'} marginTop={0.5}>
                    {description}
                </Typography>
            )}
        </Grid>
    );
};

const momentPeriods: { [key: string]: string } = {
    minutes: 'Minutes',
    hours: 'Hours',
    days: 'Days',
    weeks: 'Weeks',
    months: 'Months',
};

/**
 * Props for the MessageUsersMassDmAutoMassDmMessageForm component.
 * @property {OnlyFansSubscriber} subscriber - The subscriber data.
 * @property {() => void} refetchMassDms - Function to refetch the Mass DMs.
 * @property {() => void} handleCancel - Function to handle the cancel action.
 * @property {(show: boolean) => void} setShowAddNewForm - Function to set the visibility of the add new form.
 */
type Props = {
    subscriber: OnlyFansSubscriber;
    setIsFormInvalid: (invalid: boolean) => void;
    formData: OnlyFansMassDMMessageSchedule;
    setFormData: (formData: OnlyFansMassDMMessageSchedule) => void;
};

/**
 * Component for creating and scheduling a Mass DM message.
 *
 * @param {Props} props - The properties for the component.
 * @param {any} props.subscriber - The subscriber data.
 * @param {() => void} props.refetchMassDms - Function to refetch the Mass DMs.
 * @param {() => void} props.handleCancel - Function to handle the cancel action.
 * @param {(show: boolean) => void} props.setShowForm - Function to set the visibility of the add new form.
 */
const MessageUsersMassDmAutoMassDmMessageForm = (props: Props) => {
    const { subscriber, setIsFormInvalid, formData, setFormData } = props;
    const theme = useTheme();

    const [timezone, setTimezone] = useState<string>(moment.tz.guess());
    const [date, setDate] = useState<moment.Moment | null>(formData.scheduleDate ? moment(formData.scheduleDate) : null);
    const [time, setTime] = useState<moment.Moment | null>(formData.scheduleDate ? moment(formData.scheduleDate) : null);
    const [repeatEndDate, setRepeatEndDate] = useState<moment.Moment | null>(
        formData.repeatEndDate ? moment(formData.repeatEndDate) : null,
    );

    // const errors = OnlyFansMassDMMessageScheduleSchema.validate(formData, { abortEarly: false }).error?.details;
    const errors = useMemo(
        () => OnlyFansMassDMMessageScheduleSchema.validate(formData, { abortEarly: false, stripUnknown: true }).error?.details,
        [formData],
    );

    const {
        lists,
        // data: listsPages,
        //     hasNextPage, isFetchingNextPage, fetchNextPage
    } = useSubscriberLists(true);

    // Fetch next page of lists when needed
    // useEffect(() => {
    //     if (hasNextPage && !isFetchingNextPage) {
    //         fetchNextPage();
    //     }
    // }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    // const lists = useMemo(() => {
    //     if (!listsPages?.pages) return [];

    //     const allLists = listsPages.pages.flatMap(page => page.list);

    //     return allLists;
    // }, [listsPages]);

    const { data: restrictedWords, isLoading: isLoadingRestrictedWords } = useSextforceBigBrotherRestrictedWords();
    const { categories: captionCategories = [] } = useSextforceCaptions(true);
    const { data: contentInventory, isLoading: isContentInventoryLoading } = useInventory();

    const scheduleType = formData.type || 'manual';

    const [name, setName] = useState<string>(formData.name ?? 'Untitled');
    const [templateSubMode, setTemplateSubMode] = useState<TemplateSubMode>(
        (formData.templateTags?.length ?? 0) > 0 ? 'tags' : 'ids',
    );
    const [userLists, setUserLists] = useState<(number | string | OnlyFansLists.Type)[]>(formData.userLists);
    const [excludedLists, setExcludedLists] = useState<(number | string | OnlyFansLists.Type)[]>(formData.excludedLists);
    const [includeMedia, setIncludeMedia] = useState<boolean>(formData.medias.length > 0);
    const [medias, setMedias] = useState<OnlyFansVaultMedias.RestructuredResponseItem[]>([]);
    const [mediasPreviews, setMediasPreviews] = useState<number[]>(formData.mediasPreviews);
    const [taggedCreators, setTaggedCreators] = useState<{ id: number; name: string }[]>(formData.taggedCreators);
    const [taggedReleaseForms, setTaggedReleaseForms] = useState<{ id: number; name: string }[]>(formData.taggedReleaseForms);
    const [thumbnails, setThumbnails] = useState<unknown[]>([]);
    const [tagCreators, setTagCreators] = useState<boolean>(formData.taggedCreators.length > 0 || formData.taggedReleaseForms.length > 0);
    const [lockMessage, setLockMessage] = useState<boolean>(!!(formData.price && formData.price > 0));
    const [lockMessagePrice, setLockMessagePrice] = useState<number>(formData.price || 0);
    const [lockedText, setLockedText] = useState<boolean>(formData.lockedText);
    const [message, setMessage] = useState<string>(formData.text);
    const [messageHasRestrictedWords, setMessageHasRestrictedWords] = useState<boolean>(false);
    const [messageFoundRestrictedWords, setMessageFoundRestrictedWords] = useState<string[]>([]);
    const [saveTemplate, setSaveTemplate] = useState<boolean>(false);
    const [saveTemplateName, setSaveTemplateName] = useState<string>('');
    const [selectedInventory, setSelectedInventory] = useState<ContentInventory>();
    const [selectedInventoryVersion, setSelectedInventoryVersion] = useState<InventoryVersion>();
    const { getManyVaultMediaFromDatabase } = useSubscribersVaultMediasAdditonal();

    useEffect(() => {
        const switchInventory = async () => {
            if (selectedInventoryVersion) {
                const oldFormData = { ...formData };

                setLockMessage(false);
                setTagCreators(false);
                setTaggedCreators([]);
                setTaggedReleaseForms([]);
                setIncludeMedia(false);
                setMedias([]);
                setMediasPreviews([]);

                if (!oldFormData.name) {
                    setName(`${selectedInventory ? `${selectedInventory.name} - ` : ''}${selectedInventoryVersion.versionName}`);
                    oldFormData.name = `${selectedInventory ? `${selectedInventory.name} - ` : ''}${selectedInventoryVersion.versionName}`;
                }

                oldFormData.medias = [];
                oldFormData.mediasPreviews = [];
                oldFormData.taggedCreators = [];
                oldFormData.taggedReleaseForms = [];
                oldFormData.price = 0;
                oldFormData.userLists =
                    selectedInventoryVersion.sendLists?.map(items => {
                        if (typeof items.listId === 'number') {
                            return items.listId;
                        }
                        return items.listId as OnlyFansLists.Type;
                    }) ?? [];

                setUserLists(oldFormData.userLists);

                oldFormData.excludedLists =
                    selectedInventoryVersion.excludeLists?.map(items => {
                        if (typeof items.listId === 'number') {
                            return items.listId;
                        }
                        return items.listId as OnlyFansLists.Type;
                    }) ?? [];

                if (selectedInventoryVersion.autoAddList && selectedInventoryVersion.autoAddList !== '') {
                    const autoAddListId = Number.parseInt(selectedInventoryVersion.autoAddList, 10);

                    if (!Number.isNaN(autoAddListId) && !oldFormData.excludedLists.includes(autoAddListId)) {
                        oldFormData.excludedLists.push(autoAddListId);
                    }
                }

                setExcludedLists(oldFormData.excludedLists);

                // setMessage(selectedInventory.text);

                if (Number(selectedInventoryVersion.price) > 0) {
                    setLockMessage(true);
                    setLockMessagePrice(Number(selectedInventoryVersion.price));
                    oldFormData.price = Number(selectedInventoryVersion.price);
                }

                if (selectedInventoryVersion.creatorTags && selectedInventoryVersion.creatorTags.length > 0) {
                    setTagCreators(true);
                    setTaggedCreators(
                        selectedInventoryVersion.creatorTags.map(item => ({ id: item.creatorOnlyfansId, name: item.creatorName })),
                    );
                    oldFormData.taggedCreators = selectedInventoryVersion.creatorTags.map(item => ({
                        id: item.creatorOnlyfansId,
                        name: item.creatorName,
                    }));
                }

                if (
                    selectedInventoryVersion.additionalSettings?.consentForms &&
                    selectedInventoryVersion.additionalSettings.consentForms.length > 0
                ) {
                    setTagCreators(true);
                    const releaseForms = selectedInventoryVersion.additionalSettings.consentForms.map(item => ({
                        id: item.id,
                        name: item.name,
                    }));
                    setTaggedReleaseForms(releaseForms);
                    oldFormData.taggedReleaseForms = releaseForms;
                }

                // setFormData(oldFormData);

                if (
                    (selectedInventoryVersion.unlockedMedias?.length ?? 0) > 0 ||
                    (selectedInventoryVersion.lockedMedias?.length ?? 0) > 0
                ) {
                    setIncludeMedia(true);

                    oldFormData.medias = [
                        ...(selectedInventoryVersion.lockedMedias?.map(item => item.mediaId) ?? []),
                        ...(selectedInventoryVersion.unlockedMedias?.map(item => item.mediaId) ?? []),
                    ];

                    const grabMedias = await getManyVaultMediaFromDatabase(oldFormData.medias);

                    setMedias(grabMedias);

                    // Preview media only makes sense for a paid (locked) message. If the inventory
                    // item has no price, include the media but don't mark any of it as preview.
                    if (Number(selectedInventoryVersion.price) > 0 && (selectedInventoryVersion.unlockedMedias?.length ?? 0) > 0) {
                        setLockMessage(true);
                        const previewMediaIds = selectedInventoryVersion.unlockedMedias?.map(item => item.mediaId) ?? [];
                        setMediasPreviews(previewMediaIds);
                        oldFormData.mediasPreviews = previewMediaIds;
                    }
                }

                if (selectedInventoryVersion.text && selectedInventoryVersion.text.length > 0) {
                    setMessage(selectedInventoryVersion.text);
                    oldFormData.text = selectedInventoryVersion.text;
                }

                // Auto-unsend after a number of hours, only when the template defines a positive value.
                // Setting both fields ticks the "After a certain amount of time" checkbox (it's derived from these).
                if (
                    selectedInventoryVersion.additionalSettings?.autoUnsendHours &&
                    selectedInventoryVersion.additionalSettings.autoUnsendHours > 0
                ) {
                    oldFormData.autoUnsendTimerPeriodAmount = selectedInventoryVersion.additionalSettings.autoUnsendHours;
                    oldFormData.autoUnsendTimerPeriod = 'hours';
                }

                setFormData(oldFormData);
            }
        };

        switchInventory();
    }, [selectedInventory, selectedInventoryVersion]);

    const { getMediaCahcedReconstructedItem } = useSubscriberMedia();

    useEffect(() => {
        if (formData.medias.length > 0) {
            const mediasPromises = formData.medias.map(mediaId => getMediaCahcedReconstructedItem(subscriber, mediaId));

            Promise.all(mediasPromises).then(results => {
                setMedias(results.filter(media => media !== null) as OnlyFansVaultMedias.RestructuredResponseItem[]);
            });
        }
    }, []);

    // Stale template selections (deleted/inactive variants, duplicates) are reconciled by
    // InventoryTemplateVariantsSelector itself, which pushes the cleaned list back via onChange.

    useEffect(() => {
        // update the formData medias, mediasPreviews, taggedCreators, taggedReleaseForms
        setFormData({
            ...formData,
            name,
            ...(scheduleType === 'template'
                ? { userLists: [], excludedLists: [], recentSendToListMinusDays: null }
                : { userLists, excludedLists }),
            medias: includeMedia ? medias.map(media => media.id) : [],
            mediasPreviews: includeMedia ? mediasPreviews : [],
            taggedCreators: tagCreators ? taggedCreators : [],
            taggedReleaseForms: tagCreators ? taggedReleaseForms : [],
            price: lockMessage ? lockMessagePrice : null,
            lockedText,
            saveTemplateName: saveTemplate ? saveTemplateName : null,
        });
    }, [
        name,
        userLists,
        excludedLists,
        includeMedia,
        medias,
        mediasPreviews,
        lockMessagePrice,
        lockMessage,
        lockedText,
        tagCreators,
        taggedCreators,
        taggedReleaseForms,
        saveTemplateName,
        saveTemplate,
        scheduleType,
    ]);

    // Handle text changes separately
    useEffect(() => {
        setFormData({
            ...formData,
            text: message,
        });
    }, [message]);

    // When loacal scheduled date time or repeat end date time changes, update the formData but take timezone into account
    useEffect(() => {
        setFormData({
            ...formData,
            ...(date && time
                ? { scheduleDate: moment.tz(`${moment(date).format('YYYY-MM-DD')} ${moment(time).format('HH:mm')}`, timezone).toDate() }
                : { scheduleDate: null }),
            ...(repeatEndDate
                ? { repeatEndDate: moment.tz(moment(repeatEndDate).format('YYYY-MM-DD HH:mm'), timezone).toDate() }
                : { repeatEndDate: undefined }),
        });
    }, [date, time, repeatEndDate, timezone]);

    // Check the message doesn't contain any OnlyFans restricted words
    const checkForRestrictedWords = useCallback(
        (text: string) => {
            if (!text || isLoadingRestrictedWords || !restrictedWords || !subscriber) {
                setMessageHasRestrictedWords(false);
                setMessageFoundRestrictedWords([]);
                return;
            }

            const restrictedWordsFound = getRestrictedWordsInMessage(subscriber, text, restrictedWords);

            setMessageHasRestrictedWords(restrictedWordsFound.length > 0);
            setMessageFoundRestrictedWords(restrictedWordsFound);
        },
        [isLoadingRestrictedWords, restrictedWords, subscriber],
    );

    const isFormInvalid: boolean =
        !!errors ||
        (scheduleType === 'manual' &&
            ((includeMedia && formData.medias.length === 0) ||
                (tagCreators && taggedCreators.length === 0 && taggedReleaseForms.length === 0) ||
                (lockMessage && lockMessagePrice < 3) ||
                (lockedText && (formData.text ?? '').trim().length === 0) ||
                ((formData.text ?? '').trim().length === 0 && formData.medias.length === 0))) ||
        (scheduleType === 'album' &&
            (!(formData.sourceAlbumId && formData.sourceAlbumId >= 1) ||
                !(formData.postImage || formData.postVideo || formData.postAudio || formData.postGif))) ||
        (scheduleType === 'template' &&
            (templateSubMode === 'ids' ? (formData.templateIds?.length ?? 0) === 0 : (formData.templateTags?.length ?? 0) === 0));

    useEffect(() => {
        setIsFormInvalid(isFormInvalid);
    }, [isFormInvalid]);

    return (
        <>
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <Grid container alignItems={'center'} justifyContent={'center'} spacing={2}>
                    <TitleGridItem title="Name" description="Name this Mass DM so it's recognisable." />
                    <Grid item xs={12}>
                        <TextField
                            id="name"
                            name="name"
                            value={name}
                            fullWidth
                            onChange={e => setName(e.target.value)}
                            error={!!errors?.find(error => error.context?.key === 'name')}
                            helperText={errors && errors.find(error => error.context?.key === 'name') ? 'Name is required' : undefined}
                            sx={{ mt: 0 }}
                        />
                    </Grid>

                    <TitleGridItem
                        title="Category Color"
                        description="Choose an identifying color for this repost. This will be used to help identify the category or topic of the repost."
                    />
                    <Grid item xs={12}>
                        <SextforceColorSelector
                            colorId={formData.colorId}
                            setColorId={colorId => setFormData({ ...formData, colorId })}
                            sx={{ mt: 0.5 }}
                        />
                    </Grid>

                    <TitleGridItem
                        title="Content Type"
                        description="Compose the message manually, pick random media from an album, use templates from content inventory, or send a random caption."
                    />
                    <Grid item xs={12}>
                        <ButtonGroup fullWidth>
                            <Button
                                variant={scheduleType === 'manual' ? 'contained' : 'outlined'}
                                color="primary"
                                size="large"
                                onClick={() => setFormData({ ...formData, type: 'manual' })}
                            >
                                Manual
                            </Button>
                            <Button
                                variant={scheduleType === 'caption' ? 'contained' : 'outlined'}
                                color="primary"
                                size="large"
                                onClick={() =>
                                    setFormData({
                                        ...formData,
                                        type: 'caption',
                                        addPurchasersToUserList: false,
                                        addPurchasersToUserListId: undefined,
                                    })
                                }
                            >
                                Caption
                            </Button>
                            <Button
                                variant={scheduleType === 'album' ? 'contained' : 'outlined'}
                                color="primary"
                                size="large"
                                onClick={() =>
                                    setFormData({
                                        ...formData,
                                        type: 'album',
                                        postImage: formData.postImage ?? true,
                                        postVideo: formData.postVideo ?? true,
                                        postAudio: formData.postAudio ?? false,
                                        postGif: formData.postGif ?? false,
                                        addPurchasersToUserList: false,
                                        addPurchasersToUserListId: undefined,
                                    })
                                }
                            >
                                Album
                            </Button>
                            <Button
                                variant={scheduleType === 'template' ? 'contained' : 'outlined'}
                                color="primary"
                                size="large"
                                onClick={() => {
                                    setUserLists([]);
                                    setExcludedLists([]);
                                    setFormData({
                                        ...formData,
                                        type: 'template',
                                        templateIds: formData.templateIds ?? [],
                                        templateTags: formData.templateTags ?? [],
                                        userLists: [],
                                        excludedLists: [],
                                        recentSendToListMinusDays: null,
                                    });
                                }}
                            >
                                Template
                            </Button>
                        </ButtonGroup>
                    </Grid>

                    {scheduleType === 'manual' && (
                        <Grid item xs={12}>
                            <QuickSelectInventory
                                setSelectedInventoryVersion={setSelectedInventoryVersion}
                                setSelectedInventory={setSelectedInventory}
                            />
                        </Grid>
                    )}

                    {scheduleType === 'album' && (
                        <>
                            <Grid item xs={12}>
                                Source Album
                                <Typography variant="body2" color={'text.secondary'} marginTop={0.5} marginBottom={0.5}>
                                    Select an album to send random media from when this Mass DM is sent.
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <OnlyFansAlbumSelector
                                    size="small"
                                    vaultAlbum={formData.sourceAlbumId ?? 0}
                                    handleAlbumChange={e => {
                                        const v = e.target.value;
                                        setFormData({
                                            ...formData,
                                            sourceAlbumId: v === '' ? undefined : Number.parseInt(v, 10),
                                        });
                                    }}
                                    disabled={false}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                Content to Include
                                <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                    Which types of content from the album should be included?
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction="column" flexWrap="wrap" gap={0} useFlexGap>
                                    <FormControl component="fieldset" sx={{ mt: 0, mb: 0 }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Checkbox
                                                size="medium"
                                                checked={!!formData.postImage}
                                                onChange={e => setFormData({ ...formData, postImage: e.target.checked })}
                                            />
                                            <Typography variant="body2">Images</Typography>
                                        </Stack>
                                    </FormControl>
                                    <FormControl component="fieldset" sx={{ mt: 0, mb: 0 }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Checkbox
                                                size="medium"
                                                checked={!!formData.postVideo}
                                                onChange={e => setFormData({ ...formData, postVideo: e.target.checked })}
                                            />
                                            <Typography variant="body2">Videos</Typography>
                                        </Stack>
                                    </FormControl>
                                    <FormControl component="fieldset" sx={{ mt: 0, mb: 0 }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Checkbox
                                                size="medium"
                                                checked={!!formData.postAudio}
                                                onChange={e => setFormData({ ...formData, postAudio: e.target.checked })}
                                            />
                                            <Typography variant="body2">Audio</Typography>
                                        </Stack>
                                    </FormControl>
                                    <FormControl component="fieldset" sx={{ mt: 0, mb: 0 }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Checkbox
                                                size="medium"
                                                checked={!!formData.postGif}
                                                onChange={e => setFormData({ ...formData, postGif: e.target.checked })}
                                            />
                                            <Typography variant="body2">GIFs</Typography>
                                        </Stack>
                                    </FormControl>
                                </Stack>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl component="fieldset" sx={{ mt: 0 }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Checkbox
                                            size="medium"
                                            checked={!!formData.postCaption}
                                            onChange={e => setFormData({ ...formData, postCaption: e.target.checked })}
                                        />
                                        <Typography variant="body2">Random Caption</Typography>
                                    </Stack>
                                </FormControl>
                            </Grid>
                            {formData.postCaption && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" color={'text.secondary'} marginBottom={0.5}>
                                        Caption category (optional)
                                    </Typography>
                                    <Select
                                        fullWidth
                                        size="small"
                                        value={formData.captionCategory || ''}
                                        onChange={e => setFormData({ ...formData, captionCategory: e.target.value })}
                                    >
                                        <MenuItem value="">All captions</MenuItem>
                                        {captionCategories.map(category => (
                                            <MenuItem key={category._id} value={category.name}>
                                                {category.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Grid>
                            )}
                        </>
                    )}

                    {scheduleType === 'caption' && (
                        <>
                            <Grid item xs={12}>
                                Caption
                                <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                    A random caption is sent as a free text-only Mass DM when this schedule runs. No media is attached.
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" color={'text.secondary'} marginBottom={0.5}>
                                    Caption category (optional)
                                </Typography>
                                <Select
                                    fullWidth
                                    size="small"
                                    value={formData.captionCategory || ''}
                                    onChange={e => setFormData({ ...formData, captionCategory: e.target.value })}
                                >
                                    <MenuItem value="">All captions</MenuItem>
                                    {captionCategories.map(category => (
                                        <MenuItem key={category._id} value={category.name}>
                                            {category.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                        </>
                    )}

                    {scheduleType === 'template' && (
                        <>
                            <Grid item xs={12}>
                                Templates
                                <Typography variant="body2" color={'text.secondary'} marginTop={0.5}>
                                    Select one or more templates from content inventory, or select one or more template tags to draw from
                                    every template carrying any of those tags. One matching template will be randomly chosen when the Mass
                                    DM is sent.
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <TemplateModeToggle
                                    value={templateSubMode}
                                    onChange={nextMode => {
                                        setTemplateSubMode(nextMode);
                                        setFormData({ ...formData, templateIds: [], templateTags: [] });
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                {templateSubMode === 'ids' ? (
                                    <InventoryTemplateVariantsSelector
                                        inventory={contentInventory}
                                        loading={isContentInventoryLoading}
                                        selectedTemplates={formData.templateIds ?? []}
                                        onChange={templateIds => setFormData({ ...formData, templateIds })}
                                    />
                                ) : (
                                    <InventoryTemplateTagsSelector
                                        inventory={contentInventory}
                                        loading={isContentInventoryLoading}
                                        value={formData.templateTags ?? []}
                                        onChange={templateTags => setFormData({ ...formData, templateTags })}
                                        paidOnly={formData.templateTagsPaidOnly ?? true}
                                        onPaidOnlyChange={paidOnly => setFormData({ ...formData, templateTagsPaidOnly: paidOnly })}
                                    />
                                )}
                                <Typography variant="body2" color="text.secondary" marginTop={1}>
                                    Send-to and exclude lists are taken from the chosen template&apos;s settings when the Mass DM is sent.
                                </Typography>
                            </Grid>
                        </>
                    )}

                    {scheduleType !== 'template' && (
                        <>
                            <TitleGridItem title="Send To" description="Select the target list(s) to send this Mass DM to." />
                            <Grid item xs={12}>
                                <OnlyFansListsMultiSelector
                                    targetLists={userLists.map(listId => {
                                        return {
                                            id: parseOnlyFansListId(listId),
                                            name: lists?.find(list => list.id === parseOnlyFansListId(listId))?.name || 'Unknown',
                                        };
                                    })}
                                    setTargetLists={targetLists =>
                                        setUserLists(targetLists.map(list => parseOnlyFansListId(list.id) ?? list.id))
                                    }
                                    recentAfterDate={
                                        formData.recentSendToListMinusDays
                                            ? moment().subtract(formData.recentSendToListMinusDays, 'days')
                                            : null
                                    }
                                    setRecentAfterDate={(date: moment.Moment | null) =>
                                        setFormData({ ...formData, recentSendToListMinusDays: date ? moment().diff(date, 'days') : null })
                                    }
                                    listItemBackgroundColor={alpha(theme.palette.primary.main, 0.3)}
                                    showSelectedListsChips={true}
                                    sx={{
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    }}
                                />
                                {errors && errors.find(error => error.context?.key === 'userLists') && (
                                    <Typography variant="body2" color={'error'} fontSize={'0.7rem'} sx={{ ml: '14px' }}>
                                        Please select at least one target list
                                    </Typography>
                                )}
                            </Grid>

                            <TitleGridItem title="Exclude" description="Select the list(s) of users to exclude from this Mass DM." />
                            <Grid item xs={12}>
                                <OnlyFansListsMultiSelector
                                    targetLists={excludedLists.map(listId => {
                                        return {
                                            id: parseOnlyFansListId(listId),
                                            name: lists?.find(list => list.id === parseOnlyFansListId(listId))?.name || 'Unknown',
                                        };
                                    })}
                                    setTargetLists={targetLists =>
                                        setExcludedLists(targetLists.map(list => parseOnlyFansListId(list.id) ?? list.id))
                                    }
                                    disabledLists={userLists}
                                    listItemBackgroundColor={alpha(theme.palette.error.main, 0.3)}
                                    showSelectedListsChips={true}
                                    sx={{
                                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                                    }}
                                />
                            </Grid>
                        </>
                    )}

                    {scheduleType === 'manual' && (
                        <>
                            <TitleGridItemWithCheckBox title="Include Media" checked={includeMedia} setChecked={setIncludeMedia} />
                            {includeMedia && (
                                <Grid item xs={12}>
                                    <Paper variant="outlined" sx={{ p: 2, backgroundColor: alpha(theme.palette.info.main, 0.1) }}>
                                        <OnlyFansMediaSelector
                                            serviceName="autoMassDm"
                                            subscriber={subscriber}
                                            medias={medias}
                                            setMedias={setMedias}
                                            mediasPreviews={mediasPreviews}
                                            setMediasPreviews={setMediasPreviews}
                                            thumbnails={thumbnails}
                                            setThumbnails={setThumbnails}
                                            tagCreators={tagCreators}
                                            setTagCreators={setTagCreators}
                                            taggedCreators={taggedCreators}
                                            setTaggedCreators={setTaggedCreators}
                                            taggedReleaseForms={taggedReleaseForms}
                                            setTaggedReleaseForms={setTaggedReleaseForms}
                                            selectSize="small"
                                        />
                                    </Paper>
                                </Grid>
                            )}

                            {includeMedia && (
                                <>
                                    <TitleGridItemWithCheckBox title="Tag Creators" checked={tagCreators} setChecked={setTagCreators} />
                                    {tagCreators && (
                                        <Grid item xs={12}>
                                            <Paper variant="outlined" sx={{ p: 2, backgroundColor: alpha(theme.palette.info.main, 0.1) }}>
                                                <OnlyFansTagCreatorsSelector
                                                    subscriber={subscriber}
                                                    tagCreators={tagCreators}
                                                    setTagCreators={setTagCreators}
                                                    taggedCreators={taggedCreators}
                                                    setTaggedCreators={setTaggedCreators}
                                                    taggedReleaseForms={taggedReleaseForms}
                                                    setTaggedReleaseForms={setTaggedReleaseForms}
                                                    session={null}
                                                    disabled={false}
                                                />
                                            </Paper>
                                        </Grid>
                                    )}
                                </>
                            )}

                            <TitleGridItemWithCheckBox title="Lock Message (PPV)" checked={lockMessage} setChecked={setLockMessage} />
                            {lockMessage && (
                                <Grid item xs={12}>
                                    <Paper variant="outlined" sx={{ p: 2, backgroundColor: alpha(theme.palette.warning.main, 0.1) }}>
                                        <MessageUsersPriceLockAndPreviewSelector
                                            subscriber={subscriber}
                                            lockMessagePrice={lockMessagePrice}
                                            setLockMessagePrice={setLockMessagePrice}
                                            lockedText={lockedText}
                                            setLockedText={setLockedText}
                                            thumbnails={thumbnails}
                                            medias={medias}
                                            mediasPreviews={mediasPreviews}
                                            setMediasPreviews={setMediasPreviews}
                                        />
                                    </Paper>
                                </Grid>
                            )}

                            <TitleGridItem
                                title="Message"
                                description="Enter the message to send below. Use the toolbar icons for styling the text in bold, italic, change
                                        its color and font size."
                            />
                            <Grid item xs={12}>
                                <OnlyFansMessageEditorWithPreview
                                    subscriber={subscriber}
                                    message={message}
                                    setMessage={setMessage}
                                    messageHasRestrictedWords={messageHasRestrictedWords}
                                    messageFoundRestrictedWords={messageFoundRestrictedWords}
                                    checkForRestrictedWords={checkForRestrictedWords}
                                    taggedCreators={taggedCreators}
                                    taggedReleaseForms={taggedReleaseForms}
                                    lockMessage={lockMessage}
                                    lockMessagePrice={formData.price || 0}
                                    lockedText={lockedText}
                                    saveTemplate={saveTemplate}
                                    setSaveTemplate={setSaveTemplate}
                                    saveTemplateName={saveTemplateName}
                                    setSaveTemplateName={setSaveTemplateName}
                                    showUsernamePlaceholder={false}
                                    setUsernameReplacement={() => {}}
                                />
                            </Grid>
                        </>
                    )}

                    <TitleGridItem
                        title="When should this Mass DM go out?"
                        description="Please note that if you've exceeded the 50 Mass DM limit, this message will not be sent."
                    />
                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2, backgroundColor: alpha(theme.palette.success.main, 0.05) }}>
                            <RadioGroup
                                name="massDmSchedule"
                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                    if (event.target.value === 'now') {
                                        setDate(null);
                                        setTime(null);
                                    } else {
                                        setDate(moment());
                                        setTime(moment());
                                    }
                                }}
                            >
                                <FormControlLabel value="now" control={<Radio />} label="Now" checked={!formData.scheduleDate} />
                                <FormControlLabel value="later" control={<Radio />} label="Later..." checked={!!formData.scheduleDate} />
                            </RadioGroup>

                            {formData.scheduleDate && (
                                <Grid container alignItems={'center'} justifyContent={'center'} spacing={1} sx={{ pl: 3 }}>
                                    <Grid item xs={12}>
                                        <SelectTimezone
                                            timezone={timezone}
                                            setTimezone={setTimezone}
                                            fullWidth={true}
                                            disabled={!formData.scheduleDate}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <MobileDatePicker
                                            slots={{
                                                textField: props => (
                                                    <TextField
                                                        fullWidth
                                                        {...props}
                                                        error={moment(formData.scheduleDate).isBefore(moment())}
                                                    />
                                                ),
                                            }}
                                            label="Date"
                                            value={moment(date)}
                                            disabled={!formData.scheduleDate}
                                            onChange={newValue => {
                                                setDate(newValue as moment.Moment);
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <MobileTimePicker
                                            label="Time"
                                            ampm={true}
                                            value={moment(time)}
                                            disabled={!formData.scheduleDate}
                                            onChange={newValue => {
                                                setTime(newValue as moment.Moment);
                                            }}
                                            slots={{
                                                textField: props => (
                                                    <TextField
                                                        fullWidth
                                                        {...props}
                                                        error={moment(formData.scheduleDate).isBefore(moment())}
                                                    />
                                                ),
                                            }}
                                            sx={{
                                                '.MuiPickersToolbar-content .Mui-selected': {
                                                    color: theme.palette.secondary.main,
                                                },
                                            }}
                                        />
                                    </Grid>
                                    {moment(formData.scheduleDate).isBefore(moment()) && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2" color={'error'} fontSize={'0.7rem'} sx={{ ml: '14px' }}>
                                                Scheduled date must be in the future.
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            )}
                        </Paper>
                    </Grid>

                    <TitleGridItemWithCheckBox
                        title="Repeat Mass DM"
                        checked={
                            !!(
                                typeof formData.repeatEveryAmount !== 'undefined' &&
                                typeof formData.repeatEveryUnit !== 'undefined' &&
                                formData.repeatEveryAmount !== null &&
                                formData.repeatEveryUnit !== null
                            )
                        }
                        setChecked={(checked: boolean) => {
                            if (checked) {
                                setFormData({ ...formData, repeatEveryAmount: 1, repeatEveryUnit: 'days' });
                            } else {
                                setFormData({ ...formData, repeatEveryAmount: undefined, repeatEveryUnit: undefined });
                            }
                        }}
                    />

                    {typeof formData.repeatEveryAmount !== 'undefined' &&
                        typeof formData.repeatEveryUnit !== 'undefined' &&
                        formData.repeatEveryAmount !== null &&
                        formData.repeatEveryUnit !== null && (
                            <Grid item xs={12}>
                                <Paper variant="outlined" sx={{ p: 2, backgroundColor: alpha(theme.palette.info.main, 0.05) }}>
                                    <Grid container spacing={1} flexGrow={1} alignItems="center">
                                        <Grid item xs={6} sm="auto">
                                            <TextField
                                                label="Every"
                                                type="number"
                                                value={formData.repeatEveryAmount}
                                                onChange={e => {
                                                    setFormData({
                                                        ...formData,
                                                        repeatEveryAmount: Number.isNaN(Number.parseInt(e.target.value, 10))
                                                            ? 0
                                                            : Number.parseInt(e.target.value, 10),
                                                    });
                                                }}
                                                InputProps={{ inputProps: { min: 1 } }}
                                            />
                                        </Grid>
                                        <Grid item xs={6} sm="auto">
                                            <FormControl fullWidth>
                                                <InputLabel>Period</InputLabel>
                                                <Select
                                                    label="Period"
                                                    value={formData.repeatEveryUnit}
                                                    onChange={e => {
                                                        setFormData({
                                                            ...formData,
                                                            repeatEveryUnit: e.target.value as moment.unitOfTime.DurationConstructor,
                                                        });
                                                    }}
                                                    fullWidth
                                                    size="small"
                                                >
                                                    {Object.keys(momentPeriods).map(item => (
                                                        <MenuItem value={item} key={item}>
                                                            {momentPeriods[item]}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        {errors && errors.find(error => error.context?.key === 'repeatEveryAmount') && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2" color={'error'} fontSize={'0.7rem'} sx={{ ml: '14px' }}>
                                                    {errors.find(error => error.context?.key === 'repeatEveryAmount')?.message}
                                                </Typography>
                                            </Grid>
                                        )}
                                        <Grid item xs={12}>
                                            Stop repeating on:
                                        </Grid>
                                        <Grid item xs={12} sm="auto">
                                            <MobileDateTimePicker
                                                slots={{
                                                    textField: props => (
                                                        <TextField
                                                            {...props}
                                                            error={!!(formData.repeatEndDate && formData.repeatEndDate < new Date())}
                                                            helperText={
                                                                formData.repeatEndDate && formData.repeatEndDate < new Date()
                                                                    ? 'End date must be in the future'
                                                                    : ''
                                                            }
                                                            fullWidth
                                                            InputProps={{
                                                                endAdornment: formData.repeatEndDate ? (
                                                                    <ClearIcon
                                                                        onClick={e => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();

                                                                            setRepeatEndDate(null);
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <CalendarMonthIcon />
                                                                ),
                                                            }}
                                                        />
                                                    ),
                                                }}
                                                label="Repeat Until"
                                                ampm={true}
                                                value={repeatEndDate ? moment(repeatEndDate) : null}
                                                minDate={moment()}
                                                onChange={newValue => {
                                                    setRepeatEndDate(newValue ? newValue : null);
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        )}

                    <TitleGridItem
                        title="Unsend Mass DM"
                        description="Automatically unsend the Mass DM after a certain amount of time or views."
                    />
                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2, backgroundColor: alpha(theme.palette.info.main, 0.05) }}>
                            <Grid container spacing={1} flexGrow={1} alignItems="center">
                                <TitleGridItemWithCheckBox
                                    title="Unsend Mass DM before repeating"
                                    checked={!!formData.autoUnsendBeforeRepeat}
                                    disabled={formData.repeatEveryAmount === undefined && formData.repeatEveryUnit === undefined}
                                    setChecked={(checked: boolean) => {
                                        if (checked) {
                                            setFormData({
                                                ...formData,
                                                autoUnsendBeforeRepeat: true,
                                            });
                                        } else {
                                            setFormData({
                                                ...formData,
                                                autoUnsendBeforeRepeat: false,
                                            });
                                        }
                                    }}
                                />

                                <TitleGridItemWithCheckBox
                                    title="After a certain amount of time"
                                    checked={
                                        !!(
                                            typeof formData.autoUnsendTimerPeriodAmount === 'number' &&
                                            typeof formData.autoUnsendTimerPeriod === 'string'
                                        )
                                    }
                                    setChecked={(checked: boolean) => {
                                        if (checked) {
                                            setFormData({
                                                ...formData,
                                                autoUnsendTimerPeriodAmount: 1,
                                                autoUnsendTimerPeriod: 'days',
                                            });
                                        } else {
                                            setFormData({
                                                ...formData,
                                                autoUnsendTimerPeriodAmount: undefined,
                                                autoUnsendTimerPeriod: undefined,
                                            });
                                        }
                                    }}
                                />
                                {typeof formData.autoUnsendTimerPeriodAmount === 'number' &&
                                    typeof formData.autoUnsendTimerPeriod === 'string' && (
                                        <Grid item xs={12}>
                                            <Grid container spacing={1} flexGrow={1} alignItems="center" sx={{ ml: 3 }}>
                                                <Grid item xs={6} sm="auto">
                                                    <TextField
                                                        label={momentPeriods[formData.autoUnsendTimerPeriod]}
                                                        type="number"
                                                        value={formData.autoUnsendTimerPeriodAmount}
                                                        onChange={e => {
                                                            setFormData({
                                                                ...formData,
                                                                autoUnsendTimerPeriodAmount: Number.isNaN(
                                                                    Number.parseInt(e.target.value, 10),
                                                                )
                                                                    ? 0
                                                                    : Number.parseInt(e.target.value, 10),
                                                            });
                                                        }}
                                                        InputProps={{ inputProps: { min: 1 } }}
                                                    />
                                                </Grid>
                                                <Grid item xs={6} sm="auto">
                                                    <FormControl fullWidth>
                                                        <InputLabel>Period</InputLabel>
                                                        <Select
                                                            label="Period"
                                                            value={formData.autoUnsendTimerPeriod}
                                                            onChange={e => {
                                                                setFormData({
                                                                    ...formData,
                                                                    autoUnsendTimerPeriod: e.target
                                                                        .value as moment.unitOfTime.DurationConstructor,
                                                                });
                                                            }}
                                                            fullWidth
                                                            size="small"
                                                        >
                                                            {Object.keys(momentPeriods).map(item => (
                                                                <MenuItem value={item} key={item}>
                                                                    {momentPeriods[item]}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                {errors && errors.find(error => error.context?.key === 'autoUnsendTimerPeriodAmount') && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="body2" color={'error'} fontSize={'0.7rem'} sx={{ ml: '14px' }}>
                                                            {
                                                                errors.find(error => error.context?.key === 'autoUnsendTimerPeriodAmount')
                                                                    ?.message
                                                            }
                                                        </Typography>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Grid>
                                    )}

                                <TitleGridItemWithCheckBox
                                    title="When the Mass DM has been viewed"
                                    checked={typeof formData.autoUnsendViews === 'number'}
                                    setChecked={(checked: boolean) => {
                                        if (checked) {
                                            setFormData({
                                                ...formData,
                                                autoUnsendViews: 1000,
                                            });
                                        } else {
                                            setFormData({
                                                ...formData,
                                                autoUnsendViews: undefined,
                                            });
                                        }
                                    }}
                                />
                                {typeof formData.autoUnsendViews === 'number' && (
                                    <Grid item xs={12} sm="auto">
                                        <Grid item xs={12}>
                                            <Grid container spacing={1} flexGrow={1} alignItems="center" sx={{ ml: 3 }}>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        label="Views"
                                                        type="number"
                                                        value={formData.autoUnsendViews}
                                                        onChange={e => {
                                                            setFormData({
                                                                ...formData,
                                                                autoUnsendViews: Number.isNaN(Number.parseInt(e.target.value, 10))
                                                                    ? 0
                                                                    : Number.parseInt(e.target.value, 10),
                                                            });
                                                        }}
                                                        InputProps={{ inputProps: { min: 1 } }}
                                                    />
                                                </Grid>
                                                {errors && errors.find(error => error.context?.key === 'autoUnsendViews') && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="body2" color={'error'} fontSize={'0.7rem'} sx={{ ml: '14px' }}>
                                                            {errors.find(error => error.context?.key === 'autoUnsendViews')?.message}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )}

                                {scheduleType !== 'album' && scheduleType !== 'caption' && scheduleType !== 'template' && (
                                    <>
                                        <TitleGridItemWithCheckBox
                                            title="Add purchasers to list before unsending"
                                            checked={!!formData.addPurchasersToUserList}
                                            disabled={
                                                !formData.autoUnsendBeforeRepeat &&
                                                !formData.autoUnsendTimerPeriodAmount &&
                                                !formData.autoUnsendTimerPeriod &&
                                                !formData.autoUnsendViews
                                            }
                                            setChecked={(checked: boolean) => {
                                                if (checked) {
                                                    setFormData({
                                                        ...formData,
                                                        addPurchasersToUserList: true,
                                                    });
                                                } else {
                                                    setFormData({
                                                        ...formData,
                                                        addPurchasersToUserList: false,
                                                    });
                                                }
                                            }}
                                        />
                                        {formData.addPurchasersToUserList && (
                                            <Grid item xs={12}>
                                                <Grid container spacing={1} flexGrow={1} alignItems="center" sx={{ ml: 3, pr: 3 }}>
                                                    <Grid item xs={12}>
                                                        <OnlyFansListsSelector
                                                            size="small"
                                                            includeBuiltInLists={false}
                                                            showCreateListButton={true}
                                                            targetList={lists?.find(
                                                                list => parseOnlyFansListId(list.id) === formData.addPurchasersToUserListId,
                                                            )}
                                                            setTargetList={listId => {
                                                                const parsedListId = listId ? parseOnlyFansListId(listId.id) : undefined;

                                                                if (listId && typeof parsedListId === 'number') {
                                                                    setFormData({
                                                                        ...formData,
                                                                        addPurchasersToUserListId: parsedListId || -1,
                                                                    });
                                                                } else {
                                                                    setFormData({
                                                                        ...formData,
                                                                        addPurchasersToUserListId: undefined,
                                                                    });
                                                                }
                                                            }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        )}
                                    </>
                                )}
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            </LocalizationProvider>
        </>
    );
};

export default MessageUsersMassDmAutoMassDmMessageForm;
